import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface OperationAlertsProps {
    autoFetchError?: string | null;
    pdfImageError?: Error | null;
    onRetryAutoFetch?: () => void;
    onRetryPdfGeneration?: () => void;
    isGeneratingPdf?: boolean;
}

/**
 * Specialized component for displaying operation-related alerts and errors
 * Centralizes error display logic for auto-fetch and PDF generation
 */
export const OperationAlerts: React.FC<OperationAlertsProps> = ({ autoFetchError, pdfImageError, onRetryAutoFetch, onRetryPdfGeneration, isGeneratingPdf = false }) => {
    const { t } = useTranslation();

    return (
        <>
            {/* Auto-fetch error display */}
            {autoFetchError && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 1 }}>
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        <strong>{t('auto_fetch_error_title') || 'Warning:'}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {autoFetchError}
                    </Typography>
                    {onRetryAutoFetch && (
                        <Button size="small" onClick={onRetryAutoFetch} sx={{ mt: 1 }}>
                            {t('retry_auto_fetch') || 'Retry'}
                        </Button>
                    )}
                </Box>
            )}

            {/* PDF generation error display */}
            {pdfImageError && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 1 }}>
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        <strong>{t('pdf_error_title') || 'PDF Generation Error:'}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {pdfImageError.message}
                    </Typography>
                    {onRetryPdfGeneration && (
                        <Button size="small" onClick={onRetryPdfGeneration} sx={{ mt: 1 }} disabled={isGeneratingPdf}>
                            {t('retry_pdf_capture') || 'Retry PDF Generation'}
                        </Button>
                    )}
                </Box>
            )}
        </>
    );
};
